import React, { useCallback } from 'react';

import classnames from 'classnames';
import { FormattedMessage } from 'react-intl';

import { DropdownMenuType } from 'ui-components/components/dropdown-menu/dropdown-menu-type';
import { InlineMessage, INLINE_MESSAGE_TYPES } from 'ui-components/components/inline-message';

import FormattedCurrency from '../../../../../components/formatted-currency';
import { INSURANCE_FIELD_CLICK } from '../../../../../constants/analytics-events';
import { CSWFieldId, getOrderDetailsFieldId } from '../../../../../constants/field-ids';
import { InsuranceProviders } from '../../../../../enums/insurance-providers';
import { useFulfillmentPlannerContext } from '../../../../../fulfillment-planner/context';
import { useTrackInsuranceEvent } from '../../../../../insurance/hooks/use-track-insurance-event';
import { EventSourceStrings } from '../../../../../tracking/tracking-constants';
import {
  useGetInsuranceAlerts,
  maxInsuredValue,
  signatureMayBeRequiredAmount,
} from '../../../../../fulfillment-planner/hooks/use-get-insurance-alerts';
import InsuranceOptionContainer from '../../../../../fulfillment-planner/insurance-option/container';
import messages from '../../../../../fulfillment-planner/insurance-option/messages';
import InsuredAmountContainer from '../../../../../fulfillment-planner/insured-amount/container';
import InsuranceTerms from '../../../../../fulfillment-planner/insured-amount/insurance-terms';
import { useIsLocalDeliveryServiceSelected } from '../../../../../fulfillment-planner/hooks/use-is-local-delivery-service-selected';
import { InsuranceTermsLink } from '../../../../../insurance/terms-link';
import { useAvailableInsuranceOptions } from '../../../../../services/insurance-service';
import { InsuranceAmountLabel, InsuranceLabel } from '../../../shared-field-labels/shared-field-labels';
import LabeledField from '../labeled-field';

import fieldsStyle from '../fields.module.less';

const useHasAvailableInsuranceOptions = () => {
  const insuranceOptions = useAvailableInsuranceOptions();
  return insuranceOptions.some((option) => option.name !== InsuranceProviders.None.name);
};

const providerFieldId = getOrderDetailsFieldId(CSWFieldId.InsuranceProvider);
const amountFieldId = getOrderDetailsFieldId(CSWFieldId.InsuranceAmount);

/**
 *
 * @param {object} props
 * @param {string} props.fulfillmentPlanId
 * @param {string} [props.rowClassName]
 * @returns {JSX.Element}
 */
const Insurance = ({ fulfillmentPlanId, isPartOfInsurancePromoExperiment, rowClassName }) => {
  const { calculateRecommendedInsuranceValue, hasInsuranceProviderSelected, insuranceProvider } = useFulfillmentPlannerContext();

  const {
    isMaxCoverageExceeded,
    selectedInsuranceOptionId,
    showHighValueWarning,
    signatureMayBeRequiredWarning,
    hasUnknownInsuranceProvider,
  } = useGetInsuranceAlerts(calculateRecommendedInsuranceValue, insuranceProvider, hasInsuranceProviderSelected);

  const isLocalDelivery = useIsLocalDeliveryServiceSelected();
  const hasAvailableInsuranceOptions = useHasAvailableInsuranceOptions();
  const trackFieldClick = useTrackInsuranceEvent(INSURANCE_FIELD_CLICK, EventSourceStrings.CSW_INSURANCE_FIELD);

  const handleProviderClick = useCallback(
    (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) {
        return;
      }
      const providerElement = document.getElementById(providerFieldId);
      if (providerElement && providerElement.contains(target)) {
        trackFieldClick({ insuranceProvider: insuranceProvider?.name });
      }
    },
    [trackFieldClick, insuranceProvider?.name],
  );

  if (isLocalDelivery) {
    return null;
  }

  if (!hasAvailableInsuranceOptions) {
    return (
      <LabeledField label={InsuranceLabel}>
        <InlineMessage type={INLINE_MESSAGE_TYPES.info}>
          <FormattedMessage {...messages.insuranceNotAvailable} />
        </InlineMessage>
      </LabeledField>
    );
  }

  return (
    <>
      {/* Insurance Provider Dropdown and Warnings */}
      <LabeledField label={InsuranceLabel} labelFor={providerFieldId}>
        <div className={classnames(fieldsStyle['first-row'], rowClassName)} onClick={handleProviderClick}>
          <InsuranceOptionContainer
            dropdownMenuType={DropdownMenuType.ComboBox}
            fulfillmentPlanId={fulfillmentPlanId}
            id={providerFieldId}
            isPartOfInsurancePromoExperiment={isPartOfInsurancePromoExperiment}
          />
        </div>
        {showHighValueWarning && (
          <InlineMessage className={classnames(fieldsStyle['second-row'], rowClassName)} type={INLINE_MESSAGE_TYPES.info}>
            <FormattedMessage {...messages.highOrderValue} />
          </InlineMessage>
        )}
        {hasUnknownInsuranceProvider && (
          <InlineMessage className={classnames(fieldsStyle['second-row'], rowClassName)} type={INLINE_MESSAGE_TYPES.error}>
            <FormattedMessage
              {...messages.unknownInsuranceProvider}
              values={{
                insuranceProviderName: <FormattedMessage {...insuranceProvider.messageDescriptor} />,
              }}
            />
          </InlineMessage>
        )}
        {!hasUnknownInsuranceProvider && (
          <InsuranceTerms
            className={classnames(fieldsStyle['second-row'], rowClassName)}
            selectedInsuranceOptionId={selectedInsuranceOptionId}
          />
        )}
      </LabeledField>
      {/* Insurance Amount Input and Warnings */}
      {hasInsuranceProviderSelected && (
        <LabeledField label={InsuranceAmountLabel} labelId={amountFieldId}>
          <InsuredAmountContainer
            className={classnames(
              fieldsStyle['currency-input'],
              fieldsStyle['first-row'],
              isMaxCoverageExceeded && fieldsStyle['max-value-alert'],
            )}
            currencySymbolClassName={fieldsStyle['currency-input-symbol']}
            labelId={amountFieldId}
            rowClassName={rowClassName}
          />
          {isMaxCoverageExceeded && (
            <InlineMessage className={classnames(fieldsStyle['second-row'], rowClassName)} type={INLINE_MESSAGE_TYPES.error}>
              <FormattedCurrency fractionDigits={0} value={maxInsuredValue}>
                {(formattedBalance) => (
                  <FormattedMessage
                    {...messages.maximumCoverage}
                    values={{
                      amount: formattedBalance,
                    }}
                  />
                )}
              </FormattedCurrency>
            </InlineMessage>
          )}
          {signatureMayBeRequiredWarning && (
            <InlineMessage
              className={classnames(isMaxCoverageExceeded ? fieldsStyle['third-row'] : fieldsStyle['second-row'], rowClassName)}
              type={INLINE_MESSAGE_TYPES.info}
            >
              <FormattedCurrency fractionDigits={0} value={signatureMayBeRequiredAmount}>
                {(formattedBalance) => (
                  <FormattedMessage
                    {...messages.signatureMayBeRequired}
                    values={{
                      amount: formattedBalance,
                      a: (chunks) => (
                        <InsuranceTermsLink insuranceProviderId={selectedInsuranceOptionId} source={EventSourceStrings.CSW_INSURANCE_FIELD}>
                          {chunks}
                        </InsuranceTermsLink>
                      ),
                    }}
                  />
                )}
              </FormattedCurrency>
            </InlineMessage>
          )}
        </LabeledField>
      )}
    </>
  );
};

export default Insurance;
